<template>
  <header
    :class="[
      'fixed top-0 left-1/2 z-100 hidden w-full max-w-416.75 -translate-x-1/2 px-10 py-5 lg:block',
      {
        'pointer-events-none': isMostScrollPositionReached || isNavigationOpen,
      },
    ]"
  >
    <div
      :class="[
        'flex items-center justify-between transition-opacity duration-300',
        { 'pointer-events-none opacity-0': isNavigationOpen },
      ]"
    >
      <div
        class="flex min-w-0 flex-1 justify-start transition-opacity duration-300"
      >
        <NuxtLink
          :to="localePath('index')"
          :class="[
            'font-header max-w-[85%] min-w-0 text-[17px]/[21px] xl:text-xl! transition-all duration-400 ease-out',
            {
              'hidden!': isHomePage,
              'pointer-events-none -translate-y-full opacity-0':
                isMostScrollPositionReached,
              'translate-y-0 opacity-100': !isMostScrollPositionReached,
            },
            isDarkTheme ? 'text-white' : 'text-black',
          ]"
          :aria-label="t('common.go.to.main.page')"
        >
          <span class="block truncate">
            <ClientOnly>{{ t('header.title') }}</ClientOnly>
          </span>
        </NuxtLink>
      </div>

      <!-- Categories Navigation Menu (Header list) -->
      <div class="pointer-events-auto flex justify-center">
        <ul
          :class="[
            'flex max-w-full items-center justify-between gap-8 rounded-[7px] px-3.75 py-3 text-white shadow-[0_4px_5px_rgba(0,0,0,0.15)] transition-colors duration-300 min-[1680px]:shadow-[0_4.667px_6.183px_rgba(0,0,0,0.15)]',
            isNavigationOpen ? 'bg-black' : 'bg-[#595959]',
          ]"
        >
          <li v-for="item in menuItems" :key="item.id">
            <NuxtLink
              v-if="!hasChildren(item) && menuItemHref(item)"
              :to="menuItemHref(item)!"
              :target="item.type === 'external' ? '_blank' : '_self'"
              class="group flex cursor-pointer flex-col items-center"
            >
              <span
                class="text-sm leading-[115%] xl:text-base/[119%] 2xl:text-[18.6px]/[124%]"
              >
                {{ item.label }}
              </span>
              <div
                :class="[
                  'mx-auto h-px w-0 bg-white transition-all duration-400 ease-in-out group-hover:w-full',
                  { 'w-full': isCurrentCategory(item) },
                ]"
              />
            </NuxtLink>
            <button
              v-else
              type="button"
              @click.stop="openNavigation(item.id)"
              class="group flex cursor-pointer flex-col items-center"
            >
              <span
                class="text-sm leading-[115%] xl:text-base/[119%] 2xl:text-[18.6px]/[124%]"
              >
                {{ item.label }}
              </span>
              <div
                :class="[
                  'mx-auto h-px w-0 bg-white transition-all duration-400 ease-in-out group-hover:w-full',
                  {
                    'w-full':
                      activeCategory === item.id || isCurrentCategory(item),
                  },
                ]"
              />
            </button>
          </li>
        </ul>
      </div>

      <div
        class="flex flex-1 items-center justify-end transition-opacity duration-300"
      >
        <div
          :class="[
            'flex items-center gap-5 transition-all duration-400 ease-out',
            isMostScrollPositionReached
              ? 'pointer-events-none -translate-y-full opacity-0'
              : 'translate-y-0 opacity-100',
            isDarkTheme ? 'text-white' : 'text-black',
          ]"
        >
          <button
            @click="handleOpenSearchModal"
            class="cursor-pointer"
            :aria-label="t('common.open.search.modal')"
          >
            <LoopIcon class="h-4.75 w-4.75" />
          </button>

          <button
            @click="changeLocale"
            class="cursor-pointer text-base leading-[112.5%] uppercase"
          >
            {{ locale === 'cs' ? 'En' : 'Cs' }}
          </button>
        </div>
      </div>
    </div>
  </header>

  <!-- Mobile header with open handlers -->
  <MobileHeader
    @open-search="handleOpenSearchModal"
    @open-navigation="openNavigation"
  />

  <!-- Search Modal -->
  <SearchModal :isOpen="isSearchOpen" @close="isSearchOpen = false" />

  <!-- Navigation Modal -->
  <NavigationModal
    :isOpen="isNavigationOpen"
    :activeCategory="activeCategory"
    :menuItems="menuItems"
    @close="handleCloseNavigationModal"
    @change-category="changeCategory"
    @open-search="handleOpenSearchModal"
  />
</template>

<script setup lang="ts">
import type { MenuItem } from '~/types'

const { t, locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()

const theme = useTheme()
const isDarkTheme = computed(() => theme.value === 'dark')

const { menuItems } = await useMenuItems()

const isHomePage = computed(() => route.path === '/')
const isMostScrollPositionReached = ref(false)
const isSearchOpen = ref(false)
const isNavigationOpen = ref(false)
const activeCategory = ref('')

const switchLocalePath = useSwitchLocalePath()

const hasChildren = (item: MenuItem) =>
  !!item.children && item.children.length > 0

const menuItemHref = (item: MenuItem) => {
  const href = item.url || item.link
  if (!href) return null
  if (item.type === 'external') return href
  return localePath(href)
}

const changeLocale = () => {
  const newLocale = locale.value === 'cs' ? 'en' : 'cs'
  window.location.replace(switchLocalePath(newLocale))
}

const isCurrentCategory = (item: MenuItem) => {
  const catHref = item.url || item.link
  if (!catHref) return false
  const localizedCatHref =
    item.type === 'external' ? catHref : localePath(catHref)
  return (
    route.path === localizedCatHref ||
    route.path.startsWith(localizedCatHref + '/')
  )
}

const handleOpenSearchModal = () => {
  handleCloseNavigationModal()
  isSearchOpen.value = true
}

const openNavigation = (category?: string) => {
  const nextCategory =
    category ||
    menuItems.value.find((item) => hasChildren(item))?.id ||
    menuItems.value[0]?.id ||
    ''

  if (!nextCategory) return

  if (isNavigationOpen.value && activeCategory.value === nextCategory) {
    isNavigationOpen.value = false
    activeCategory.value = ''
  } else {
    activeCategory.value = nextCategory
    isNavigationOpen.value = true
  }
}

const handleCloseNavigationModal = () => {
  isNavigationOpen.value = false
  activeCategory.value = ''
}

const changeCategory = (category: string) => {
  if (activeCategory.value === category) {
    handleCloseNavigationModal()
    return
  }

  activeCategory.value = category
}

onMounted(() => {
  window.addEventListener('scroll', () => {
    const scrolledPosition = window.scrollY
    const scrollableHeight = window.innerHeight * 0.02
    const progress = Math.min(1, scrolledPosition / scrollableHeight)

    isMostScrollPositionReached.value = progress >= 1
  })
})
</script>
